import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Faq, PricingFeatureRow, PricingPlan } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Container } from '@/components/generals/container'
import { RevealText } from '@/components/generals/reveal-text'
import { PageHero } from '@/components/sections/page-hero'
import { PricingPlans } from '@/components/sections/pricing-plans'
import { ComparisonTable } from '@/components/sections/comparison-table'
import { FaqSection } from '@/components/sections/faq-section'

import {
    getFaqs,
    getPricingFeatures,
    getPricingPlans,
    safely
} from '@/lib/content'

interface PricingProps {
    plans: Array<PricingPlan>
    rows: Array<PricingFeatureRow>
    faqs: Array<Faq>
}

const Pricing: FC<PricingProps> = ({ plans, rows, faqs }): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Pricing'
                description='Simple, transparent pricing for Veyra. Starter and Growth plans with a 14-day free trial, and Enterprise plans tailored to complex finance organizations.'
            />

            <PageHero
                eyebrow='Pricing'
                align='center'
                title={<>Simple pricing that <span className='text-stone'>grows with you.</span></>}
                description='Start with a 14-day free trial on Starter or Growth, no credit card required. Upgrade, downgrade or cancel at any time.'
                className='pb-0'
            >
                <PricingPlans plans={plans} compareLink={rows.length > 0} reveal={false} />
            </PageHero>

            {rows.length > 0 && (
                <section id='compare' aria-labelledby='compare-title' className='scroll-mt-20 bg-cream'>
                    <Container className='pb-28 pt-24 md:pb-36 md:pt-32'>
                        <div className='mb-12 max-w-2xl'>
                            <Eyebrow>Compare plans</Eyebrow>

                            <RevealText
                                as='h2'
                                id='compare-title'
                                text='Every feature, side by side.'
                                className='mt-6 font-heading text-display-lg font-medium text-burgundy'
                            />
                        </div>

                        <div data-reveal=''>
                            <ComparisonTable rows={rows} plans={plans} />
                        </div>
                    </Container>
                </section>
            )}

            <FaqSection
                faqs={faqs}
                title='Pricing questions, answered.'
                intro='Billing, trials, plan changes and everything else you might want to know before you start.'
                className='bg-paper'
            />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<PricingProps> = async () => {
    const [plans, rows, faqs] = await Promise.all([
        safely(getPricingPlans, [], 'pricing plans'),
        safely(getPricingFeatures, [], 'pricing comparison'),
        safely(() => getFaqs(), [], 'FAQs')
    ])

    return {
        props: { plans, rows, faqs },
        revalidate: 60
    }
}

export default Pricing
